import React, { useEffect, useState } from 'react';
import { Modal, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import {
  CharacterUnlockRosterItem,
  CharacterUnlockRosterResponse,
  quizService,
} from '../services/api';
import {
  quizQuestionImageUrlsFromKey,
  resolveQuizImageAssetsFolder,
  WITC_SILHOUETTE_FILTER_CSS,
} from '../utils/quizQuestionImageSrc';
import './QuizPlayer.css';
import './PvpCharacterUnlockRosterModal.css';

/** Carrega uma URL em memória (cache do browser) e espera decode para evitar “flash” ao pintar. */
function preloadImageUrl(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      if (typeof img.decode === 'function') {
        void img
          .decode()
          .then(() => resolve(true))
          .catch(() => resolve(true));
      } else {
        resolve(true);
      }
    };
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

/** Mesma ordem WebP→PNG que {@link SilhouetteThumb}; falhas não bloqueiam o modal. */
async function preloadThumbForKey(basePath: string, imageKey: string | null | undefined): Promise<void> {
  const urls = quizQuestionImageUrlsFromKey(basePath, imageKey);
  if (!urls) return;
  const webpOk = await preloadImageUrl(urls.webp);
  if (webpOk) return;
  await preloadImageUrl(urls.png);
}

async function preloadCharacterUnlockRosterThumbnails(
  basePath: string,
  items: CharacterUnlockRosterItem[]
): Promise<void> {
  const trimmed = basePath.trim();
  if (!trimmed || items.length === 0) return;
  await Promise.all(items.map((item) => preloadThumbForKey(trimmed, item.imageAssetKey)));
}

function SilhouetteThumb({
  basePath,
  imageKey,
  maskAsSilhouette,
}: {
  basePath: string;
  imageKey: string | null | undefined;
  /** When false, full-colour image (user already unlocked this character). */
  maskAsSilhouette: boolean;
}) {
  const urls = quizQuestionImageUrlsFromKey(basePath, imageKey);
  const [src, setSrc] = useState<string | null>(urls?.webp ?? urls?.png ?? null);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    const u = quizQuestionImageUrlsFromKey(basePath, imageKey);
    setSrc(u?.webp ?? u?.png ?? null);
    setBroken(false);
  }, [basePath, imageKey]);

  const showImage = urls && !broken;
  const effectiveSrc = src || urls?.png;

  return (
    <div className="pvp-unlock-roster-silhouette-wrap">
      {showImage && effectiveSrc ? (
        <img
          src={effectiveSrc}
          alt=""
          decoding="async"
          className={
            maskAsSilhouette
              ? 'witc-image-silhouette witc-image-silhouette--masked-visible d-block pvp-unlock-roster-silhouette-img'
              : 'd-block pvp-unlock-roster-revealed-img'
          }
          style={maskAsSilhouette ? { filter: WITC_SILHOUETTE_FILTER_CSS } : undefined}
          onError={() => {
            if (urls && src === urls.webp) {
              setSrc(urls.png);
            } else {
              setBroken(true);
            }
          }}
        />
      ) : (
        <span className="pvp-unlock-roster-missing-img text-muted small user-select-none" aria-hidden>
          —
        </span>
      )}
    </div>
  );
}

export interface PvpCharacterUnlockRosterModalProps {
  show: boolean;
  quizId: string | null;
  /** Same line as lobby cards: title, or `title — theme` when theme exists. */
  quizCaption: string;
  /** Same folder as WITC/MC play mode — used if GET roster returns an empty imageAssetsBasePath. */
  imageAssetsBasePathFallback?: string | null;
  onHide: () => void;
}

const PvpCharacterUnlockRosterModal: React.FC<PvpCharacterUnlockRosterModalProps> = ({
  show,
  quizId,
  quizCaption,
  imageAssetsBasePathFallback,
  onHide,
}) => {
  const { t } = useSafeTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CharacterUnlockRosterResponse | null>(null);
  const [supplementalBasePath, setSupplementalBasePath] = useState<string | null>(null);

  useEffect(() => {
    if (!show || !quizId) {
      setData(null);
      setError(null);
      setLoading(false);
      setSupplementalBasePath(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setSupplementalBasePath(null);
    void (async () => {
      try {
        const roster = await quizService.getCharacterUnlockRoster(quizId);
        if (cancelled) return;

        let supplemental: string | null = null;
        const apiPath = (roster.imageAssetsBasePath ?? '').trim();
        const fb = (imageAssetsBasePathFallback ?? '').trim();
        if (!apiPath && !fb) {
          try {
            const qz = await quizService.getActiveQuiz(quizId);
            if (cancelled) return;
            supplemental = resolveQuizImageAssetsFolder(qz).trim() || null;
          } catch {
            supplemental = null;
          }
        }

        const rosterBasePath = apiPath || fb || supplemental || '';
        await preloadCharacterUnlockRosterThumbnails(rosterBasePath, roster.items ?? []);

        if (cancelled) return;

        setSupplementalBasePath(supplemental);
        setData(roster);
      } catch {
        if (!cancelled) {
          setError(t('pvp.characterUnlockRosterLoadError', 'Could not load unlock info.'));
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, quizId, t, imageAssetsBasePathFallback]);

  const rosterBasePath =
    (data?.imageAssetsBasePath ?? '').trim() ||
    (imageAssetsBasePathFallback ?? '').trim() ||
    (supplementalBasePath ?? '').trim() ||
    '';

  const tooltipBody = (item: CharacterUnlockRosterItem) => {
    if (item.unlockKind === 'difficulty_band') {
      return (
        <div className="small text-start">
          <div className="fw-semibold mb-1">
            {t('pvp.characterUnlockTooltipTitle', 'Unlock requirement')}
          </div>
          <div>{item.requirementSummaryPt?.trim() || '—'}</div>
        </div>
      );
    }
    return (
      <div className="small text-start">
        <div className="fw-semibold mb-1">
          {t('pvp.characterUnlockTooltipTitle', 'Unlock requirement')}
        </div>
        <div>
          {t('pvp.characterUnlockTooltipRequirementLine', {
            count: item.timesRequired,
            answer: item.prerequisiteAnswerSummaryPt || '—',
            defaultValue: 'Answer {{count}}× correctly: {{answer}}',
          })}
        </div>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title as="h5" className="me-2">
          {t('pvp.characterUnlockModalTitle', 'Unlockable characters')}
          {quizCaption.trim() ? (
            <span className="d-block small text-muted fw-normal mt-1 text-break">{quizCaption.trim()}</span>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body aria-busy={loading}>
        {loading ? (
          <div className="text-center py-5 pvp-unlock-roster-modal-loading" role="status">
            <Spinner animation="border" aria-hidden />
            <span className="visually-hidden">{t('common.loading', 'Loading...')}</span>
          </div>
        ) : error ? (
          <p className="text-danger mb-0">{error}</p>
        ) : data && data.items.length > 0 ? (
          <div className="pvp-unlock-roster-strip d-flex flex-nowrap gap-3 overflow-auto pb-1">
            {data.items.map((item) => (
              <div key={item.unlockableQuestionId} className="pvp-unlock-roster-cell flex-shrink-0">
                <OverlayTrigger
                  placement="top"
                  trigger={['hover', 'focus']}
                  overlay={
                    <Tooltip id={`unlock-${item.unlockableQuestionId}`}>
                      {tooltipBody(item)}
                    </Tooltip>
                  }
                >
                  <button
                    type="button"
                    className="pvp-unlock-roster-thumb-btn btn btn-link p-0 border-0 shadow-none"
                    aria-label={
                      item.isUnlockedForViewer
                        ? t('pvp.characterUnlockThumbAriaUnlocked', 'Unlocked character')
                        : t('pvp.characterUnlockThumbAria', 'Unlockable silhouette')
                    }
                  >
                    <div className="pvp-unlock-roster-thumb-wrap">
                      <div className="pvp-unlock-roster-thumb">
                        <SilhouetteThumb
                          basePath={rosterBasePath}
                          imageKey={item.imageAssetKey}
                          maskAsSilhouette={!item.isUnlockedForViewer}
                        />
                      </div>
                      {item.isUnlockedForViewer ? (
                        <span
                          className="pvp-unlock-roster-unlocked-badge"
                          title={t('pvp.characterUnlockUnlockedBadgeTitle', 'Unlocked')}
                          aria-hidden
                        >
                          <i className="bi bi-check-circle-fill" />
                        </span>
                      ) : null}
                    </div>
                  </button>
                </OverlayTrigger>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted mb-0">
            {t('pvp.characterUnlockRosterUnavailable', 'No unlock preview for this quiz.')}
          </p>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default PvpCharacterUnlockRosterModal;
